import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DataExport.css';

function DataExport({ isOpen, onClose, token, api }) {
  const [job, setJob] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [memoryQuery, setMemoryQuery] = useState('');
  const [memoryResults, setMemoryResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const flash = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${api}/export/status`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        if (data.status !== 'none') setJob(data);
        else setJob(null);
      }
    } catch (e) { console.error('Failed to fetch export status:', e); }
  }, [api, headers]);

  // Fetch status on open
  useEffect(() => {
    if (isOpen && token) fetchStatus();
  }, [isOpen, token, fetchStatus]);

  // Poll while pending/running
  useEffect(() => {
    if (job && (job.status === 'pending' || job.status === 'running')) {
      pollRef.current = setInterval(fetchStatus, 2000);
      return () => clearInterval(pollRef.current);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [job, fetchStatus]);

  const handleRequestExport = async () => {
    setRequesting(true);
    try {
      const res = await fetch(`${api}/export/request`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json();
      if (data.error === 'rate_limited') {
        flash('Export limit: 1 per 24 hours. Your previous export is still available.', 'error');
        if (data.existing_job) setJob(data.existing_job);
      } else {
        setJob({ id: data.job_id, status: 'pending' });
        flash('Export started — this may take a moment…');
      }
    } catch { flash('Failed to start export', 'error'); }
    setRequesting(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${api}/export/download`, { headers: headers() });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `heartfelt-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flash('Download started');
    } catch { flash('Download failed', 'error'); }
    setDownloading(false);
  };

  const handleMemorySearch = async () => {
    if (!memoryQuery.trim()) { setMemoryResults(null); return; }
    setSearching(true);
    try {
      const res = await fetch(`${api}/memories/search?q=${encodeURIComponent(memoryQuery)}`, { headers: headers() });
      const data = await res.json();
      setMemoryResults(data.results || []);
    } catch { flash('Search failed', 'error'); }
    setSearching(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (!isOpen) return null;

  const isRunning = job && (job.status === 'pending' || job.status === 'running');
  const isReady = job && job.status === 'completed';
  const isFailed = job && job.status === 'failed';
  const isExpired = job && job.status === 'expired';

  return (
    <div className="de-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="de-panel">
        {toast && <div className={`de-toast ${toast.type}`}>{toast.text}</div>}

        <div className="de-header">
          <h2 className="de-title">Your Data</h2>
          <button className="de-close" onClick={onClose}>&times;</button>
        </div>

        {/* Full Export */}
        <div className="de-section">
          <div className="de-section-title">Full Data Export</div>
          <p className="de-section-desc">
            Download all your data as a ZIP file: profile, preferences, memories, conversations, soul profile, soul traits, wisdom entries, and geo-memories.
          </p>

          {isRunning && (
            <div className="de-progress">
              <div className="de-progress-bar">
                <div className="de-progress-fill" />
              </div>
              <span className="de-progress-text">Preparing your export…</span>
            </div>
          )}

          {isReady && (
            <div className="de-ready">
              <div className="de-ready-info">
                <span>Export ready — {formatSize(job.file_size)}</span>
                {job.expires_at && <span className="de-ready-expires">Expires {formatDate(job.expires_at)}</span>}
              </div>
              <button className="de-download-btn" onClick={handleDownload} disabled={downloading}>
                {downloading ? 'Downloading…' : 'Download ZIP'}
              </button>
            </div>
          )}

          {isFailed && (
            <div className="de-error-msg">Export failed: {job.error || 'Unknown error'}. You can try again.</div>
          )}

          {isExpired && (
            <div className="de-error-msg">Your previous export has expired. Request a new one below.</div>
          )}

          {(!isRunning) && (
            <button className="de-export-btn" onClick={handleRequestExport} disabled={requesting || isRunning}>
              {requesting ? 'Starting…' : isReady ? 'Request New Export' : 'Export All My Data'}
            </button>
          )}

          <p className="de-rate-note">Limit: 1 export per 24 hours. Download link valid for 24 hours.</p>
        </div>

        {/* Memory Search */}
        <div className="de-section">
          <div className="de-section-title">Memory Search</div>
          <p className="de-section-desc">Search through your saved memories by keyword.</p>
          <div className="de-search-row">
            <input
              className="de-search-input"
              placeholder="Search memories…"
              value={memoryQuery}
              onChange={(e) => setMemoryQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleMemorySearch(); }}
            />
            <button className="de-search-btn" onClick={handleMemorySearch} disabled={searching}>
              {searching ? '…' : 'Search'}
            </button>
          </div>

          {memoryResults !== null && (
            <div className="de-results">
              <div className="de-results-header">
                <span>{memoryResults.length} result{memoryResults.length !== 1 ? 's' : ''}</span>
                <button className="de-clear-btn" onClick={() => { setMemoryResults(null); setMemoryQuery(''); }}>
                  Clear
                </button>
              </div>
              {memoryResults.length === 0 ? (
                <div className="de-no-results">No memories match your search.</div>
              ) : (
                <div className="de-results-list">
                  {memoryResults.map((m) => (
                    <div key={m.id} className="de-result-card">
                      <div className="de-result-header">
                        <span className="de-result-category">{m.category}</span>
                        <span className="de-result-date">
                          {m.timestamp ? new Date(m.timestamp).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <p className="de-result-text">{m.text}</p>
                      {m.labels && m.labels.length > 0 && (
                        <div className="de-result-labels">
                          {(Array.isArray(m.labels) ? m.labels : []).map(l => (
                            <span key={l} className="de-result-label">{l}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Privacy info */}
        <div className="de-privacy">
          Your data belongs to you. You can export or delete it anytime from Account Settings.
        </div>
      </div>
    </div>
  );
}

export default DataExport;
